/* eslint-disable no-param-reassign */
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {http} from 'hiya-common/src/utils/axios'
import {useSelector} from 'react-redux'

import type {
  MidYearAnchorCompetition2026State,
  MidYearAnchorRaceChallengeInfoData,
  MidYearAnchorRaceChallengeRecordsData,
  MidYearAnchorRaceGiftPopularityTicketData,
  MidYearAnchorRaceLotteryInfoData,
  MidYearAnchorRaceLotteryResultData,
  MidYearAnchorRaceRankSearchData,
  MidYearAnchorRaceRevivalInfoData,
  MidYearAnchorRaceTasksData,
  MidYearAnchorRaceVoteInfoData,
} from './apiTypes'

export const get_homepage = createAsyncThunk<
  MidYearAnchorCompetition2026State & {view_stage: number},
  {stage?: number} | void
>('MidYearAnchorCompetition2026/get_homepage', async (arg, thunkApi: any) => {
  const slice = thunkApi.getState()?.MidYearAnchorCompetition2026 as
    | MidYearAnchorCompetition2026State
    | undefined
  const stage =
    arg && typeof arg === 'object' && arg.stage !== undefined
      ? arg.stage
      : slice?.view_stage ?? 0
  const body = stage > 0 ? {stage} : {}
  const resp = await http.post(
    '/live/activity/mid_year_anchor_race_homepage',
    body,
    {store: thunkApi}
  )
  return {
    ...(resp.data?.data as MidYearAnchorCompetition2026State),
    view_stage: stage,
  }
})

export const get_rules = createAsyncThunk(
  'MidYearAnchorCompetition2026/get_rules',
  async (_, store: any) => {
    const resp = await http.post(
      '/live/activity/mid_year_anchor_race_rules',
      {},
      {store}
    )
    return resp.data?.data
  }
)

export const search_rank_thunk = createAsyncThunk<
  MidYearAnchorRaceRankSearchData | undefined,
  {search_mid: number}
>('MidYearAnchorCompetition2026/search_rank', async ({search_mid}, store) => {
  const resp = await http.post(
    '/live/activity/mid_year_anchor_race_rank_search',
    {search_mid},
    {store}
  )
  return resp.data?.data as MidYearAnchorRaceRankSearchData | undefined
})

export const fetch_vote_info_thunk = createAsyncThunk<
  MidYearAnchorRaceVoteInfoData | undefined,
  {to_mid: number}
>('MidYearAnchorCompetition2026/fetch_vote_info', async ({to_mid}, store) => {
  const resp = await http.post(
    '/live/activity/mid_year_anchor_race_vote_info',
    {to_mid},
    {store}
  )
  return resp.data?.data as MidYearAnchorRaceVoteInfoData | undefined
})

export const gift_popularity_ticket_thunk = createAsyncThunk<
  MidYearAnchorRaceGiftPopularityTicketData | undefined,
  {to_mid: number; num: number}
>(
  'MidYearAnchorCompetition2026/gift_popularity_ticket',
  async ({to_mid, num}, store) => {
    const resp = await http.post(
      '/live/activity/mid_year_anchor_race_gift_popularity_ticket',
      {to_mid, num},
      {store}
    )
    return resp.data?.data as
      | MidYearAnchorRaceGiftPopularityTicketData
      | undefined
  }
)

export const fetch_revival_info_thunk = createAsyncThunk<
  MidYearAnchorRaceRevivalInfoData | undefined,
  void
>('MidYearAnchorCompetition2026/fetch_revival_info', async (_, store) => {
  const resp = await http.post(
    '/live/activity/mid_year_anchor_race_revival_info',
    {},
    {store}
  )
  return resp.data?.data as MidYearAnchorRaceRevivalInfoData | undefined
})

export const fetch_challenge_info_thunk = createAsyncThunk<
  MidYearAnchorRaceChallengeInfoData | undefined,
  void
>('MidYearAnchorCompetition2026/fetch_challenge_info', async (_, store) => {
  const resp = await http.post(
    '/live/activity/mid_year_anchor_race_challenge_info',
    {},
    {store}
  )
  return resp.data?.data as MidYearAnchorRaceChallengeInfoData | undefined
})

export const fetch_tasks_thunk = createAsyncThunk<
  MidYearAnchorRaceTasksData | undefined,
  void
>('MidYearAnchorCompetition2026/fetch_tasks', async (_, store) => {
  const resp = await http.post(
    '/live/activity/mid_year_anchor_race_tasks',
    {},
    {store}
  )
  return resp.data?.data as MidYearAnchorRaceTasksData | undefined
})

export const fetch_lottery_info_thunk = createAsyncThunk<
  MidYearAnchorRaceLotteryInfoData | undefined,
  {lottery_id: number}
>(
  'MidYearAnchorCompetition2026/fetch_lottery_info',
  async ({lottery_id}, store) => {
    const resp = await http.post(
      '/live/commonactivity/get_lottery_info',
      {lottery_id},
      {store}
    )
    return resp.data?.data as MidYearAnchorRaceLotteryInfoData | undefined
  }
)

export const post_lottery_thunk = createAsyncThunk<
  MidYearAnchorRaceLotteryResultData | undefined,
  {lottery_id: number; times: number}
>(
  'MidYearAnchorCompetition2026/post_lottery',
  async ({lottery_id, times}, store) => {
    const resp = await http.post(
      '/live/commonactivity/lottery',
      {lottery_id, times},
      {store}
    )
    return resp.data?.data as MidYearAnchorRaceLotteryResultData | undefined
  }
)

export const fetch_challenge_records_thunk = createAsyncThunk<
  MidYearAnchorRaceChallengeRecordsData | undefined,
  void
>('MidYearAnchorCompetition2026/fetch_challenge_records', async (_, store) => {
  const resp = await http.post(
    '/live/activity/mid_year_anchor_race_challenge_records',
    {},
    {store}
  )
  return resp.data?.data as MidYearAnchorRaceChallengeRecordsData | undefined
})

const initialState: MidYearAnchorCompetition2026State = {
  vote_refresh_seq: 0,
}

export const MidYearAnchorCompetition2026Slice = createSlice({
  name: 'MidYearAnchorCompetition2026',
  initialState,
  reducers: {
    bump_vote_refresh(state) {
      state.vote_refresh_seq = (state.vote_refresh_seq ?? 0) + 1
    },
  },
  extraReducers: (builder) => {
    builder.addCase(get_homepage.fulfilled, (state, action) => {
      Object.assign(state, {
        ...action.payload,
        homepageLoaded: true,
      })
    })
    builder.addCase(get_rules.fulfilled, (state, action) => {
      state.rules_data = action.payload
      console.log(state.rules_data, 'rules_data')
      state.rulesLoaded = true
    })
  },
})

export default MidYearAnchorCompetition2026Slice.reducer

export const {bump_vote_refresh} = MidYearAnchorCompetition2026Slice.actions

export const useReducerState = () => {
  return useSelector(
    (state: {
      MidYearAnchorCompetition2026?: MidYearAnchorCompetition2026State
    }) => state.MidYearAnchorCompetition2026 ?? initialState
  )
}
